/**
 * Voices page (dialog).
 *
 * One row per app language, one best voice each — not the engine's full
 * catalog of variants. Each row shows whether its model is already on this
 * device, downloads it on tap (from the app's own hosting, then cached
 * offline), and speaks a sample line so the choice can be heard before a
 * conversation depends on it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { LanguageCode } from "@shared/types/language";
import { languageLabel } from "@shared/languages";
import {
  DEFAULT_VOICE_LANGUAGES,
  R2_HOSTED_VOICES,
  describePiperVoice,
  resolvePiperVoiceId,
  type PiperGender,
} from "../lib/piper";
import type { TranslationSession } from "../lib/use-session";
import { Badge, Button, Modal, Segmented } from "./ui";
import { sampleLine } from "./PiperVoiceControl";
import { IconCheck, IconDownload, IconPlay } from "./icons";

interface Props {
  open: boolean;
  onClose: () => void;
  session: TranslationSession;
  gender: PiperGender;
  onGenderChange: (gender: PiperGender) => void;
}

export function VoicesDialog(props: Props) {
  const { session } = props;
  const [cached, setCached] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<LanguageCode | null>(null);
  const [testing, setTesting] = useState<LanguageCode | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const rows = useMemo(
    () =>
      DEFAULT_VOICE_LANGUAGES.map((code) => ({
        code,
        resolved: resolvePiperVoiceId({ language: code, gender: props.gender, customVoiceId: null, available: null }),
      })),
    [props.gender],
  );

  const refresh = useCallback(() => {
    void session.listCachedVoices().then((ids) => setCached(new Set(ids))).catch(() => undefined);
  }, [session]);

  // Refresh the on-device inventory whenever the page opens. In-flight
  // downloads clear their own flags when they settle, so there is nothing
  // to reset here (and resetting state synchronously in an effect is a
  // cascading-render trap).
  useEffect(() => {
    if (props.open) refresh();
  }, [props.open, refresh]);

  const downloadable = rows.filter((row) => row.resolved.voiceId);
  const cachedCount = downloadable.filter((row) => row.resolved.voiceId && cached.has(row.resolved.voiceId)).length;

  const onDownload = (code: LanguageCode): void => {
    if (busy) return;
    setBusy(code);
    void session
      .ensurePiperVoiceFor(code)
      .catch(() => undefined)
      .finally(() => {
        setBusy(null);
        refresh();
      });
  };

  const onTest = (code: LanguageCode): void => {
    if (testing) return;
    setTesting(code);
    void session
      .testVoice(code, sampleLine(code))
      .catch(() => undefined)
      .finally(() => setTesting(null));
  };

  const onDownloadAll = (): void => {
    if (bulkBusy) return;
    setBulkBusy(true);
    void (async () => {
      for (const row of downloadable) {
        if (row.resolved.voiceId && !cached.has(row.resolved.voiceId)) {
          await session.ensurePiperVoiceFor(row.code).catch(() => undefined);
        }
      }
      refresh();
      setBulkBusy(false);
    })();
  };

  return (
    <Modal open={props.open} title="Voices" onClose={props.onClose} wide>
      <div className="row-between wrap" style={{ marginBottom: "var(--space-s)" }}>
        <Segmented
          label="Preferred voice gender"
          options={[
            { value: "female", label: "Female" },
            { value: "male", label: "Male" },
          ]}
          value={props.gender}
          onChange={props.onGenderChange}
        />
        <span className="dim" style={{ fontSize: "var(--step--1)" }}>
          {cachedCount}/{downloadable.length} on this device
        </span>
      </div>

      <div className="stack" style={{ gap: "var(--space-2xs)" }}>
        {rows.map((row) => {
          const voiceId = row.resolved.voiceId;
          const isCached = voiceId !== null && cached.has(voiceId);
          const isBusy = busy === row.code;
          const progress = isBusy && session.piper.voiceId === voiceId && session.piper.status === "downloading" ? session.piper.progress : null;
          return (
            <div key={row.code} className="row-between wrap inset" style={{ padding: "var(--space-2xs) var(--space-s)" }}>
              <div className="stack" style={{ gap: 0, minWidth: 0 }}>
                <strong style={{ fontSize: "var(--step-0)" }}>{languageLabel(row.code)}</strong>
                <span className="dim" style={{ fontSize: "var(--step--1)" }}>
                  {voiceId ? describePiperVoice(voiceId) : "Built-in voice — no Piper model in this release"}
                </span>
                {row.resolved.note ? (
                  <span className="dim" style={{ fontSize: "var(--step--2)" }}>{row.resolved.note}</span>
                ) : null}
              </div>
              <div className="row" style={{ gap: "var(--space-2xs)" }}>
                {voiceId ? (
                  isCached ? (
                    <Badge tone="good"><IconCheck size={11} /> On device</Badge>
                  ) : isBusy ? (
                    <Badge tone="accent">{progress !== null && progress > 0 ? `${Math.round(progress * 100)}%` : "working…"}</Badge>
                  ) : (
                    <Badge>Not downloaded</Badge>
                  )
                ) : (
                  <Badge>Built-in</Badge>
                )}
                {voiceId && !isCached ? (
                  <Button size="sm" onClick={() => onDownload(row.code)} disabled={busy !== null || bulkBusy} busy={isBusy}>
                    <IconDownload size={14} /> {R2_HOSTED_VOICES.has(voiceId) ? "Download" : "Fetch"}
                  </Button>
                ) : null}
                <Button size="sm" iconOnly onClick={() => onTest(row.code)} disabled={testing !== null} title={`Hear ${languageLabel(row.code)}`} aria-label={`Hear ${languageLabel(row.code)}`}>
                  <IconPlay size={14} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="row-between wrap" style={{ marginTop: "var(--space-s)" }}>
        <span className="dim" style={{ fontSize: "var(--step--2)", maxWidth: "52ch" }}>
          One best voice per language, hosted by the app and cached on this device — speech is instant and offline afterwards.
          Thai, Hindi, Japanese and Korean have no Piper model in this engine release, so they use the built-in voices.
        </span>
        <Button onClick={onDownloadAll} disabled={bulkBusy || busy !== null} busy={bulkBusy}>
          <IconDownload size={15} /> Download all
        </Button>
      </div>
    </Modal>
  );
}
